import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { DayPicker } from 'react-day-picker';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

interface DatePickerProps {
  label?: string;
  value?: Date | null;
  onSelectDate?: (date: Date | undefined) => void;
  required?: boolean;
  disabled?: boolean;
}

export function DatePicker({ label, value, onSelectDate, required, disabled }: DatePickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-semibold text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="inline-flex items-center justify-between w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={value ? 'text-gray-900' : 'text-gray-500'}>
              {value ? value.toLocaleDateString() : 'Pick a date'}
            </span>
            <Calendar className="h-4 w-4 text-gray-500" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={4}
            className="z-50 rounded-md border border-gray-200 bg-white p-3 shadow-md animate-in fade-in-0 zoom-in-95"
          >
            <DayPicker
              mode="single"
              selected={value || undefined}
              onSelect={(date) => {
                onSelectDate?.(date);
                setOpen(false);
              }}
              components={{
                Chevron: ({ orientation }) =>
                  orientation === 'left' ? (
                    <ChevronLeft className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  ),
              }}
              classNames={{
                root: 'text-sm',
                months: 'flex flex-col',
                month_caption: 'flex justify-center items-center h-8 font-medium text-gray-900',
                nav: 'flex items-center justify-between absolute inset-x-0 top-0 h-8 px-1',
                button_previous: 'p-1 rounded hover:bg-gray-100',
                button_next: 'p-1 rounded hover:bg-gray-100',
                weekdays: 'grid grid-cols-7 text-xs font-medium text-gray-500',
                weekday: 'flex items-center justify-center h-8 w-8',
                weeks: '',
                week: 'grid grid-cols-7',
                day: 'flex items-center justify-center h-8 w-8 text-sm rounded hover:bg-primary/10 cursor-pointer',
                day_button: 'h-full w-full flex items-center justify-center',
                selected: 'bg-primary text-white hover:bg-primary',
                today: 'font-bold text-primary',
                month_grid: '',
              }}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
